
import React, { useState, useEffect } from 'react';
import { User, AppConfig } from '../types';
import { dbService, authService } from '../lib/firebase';
import { getAuth } from 'firebase/auth';
import { User as UserIcon, Mail, Lock, Shield, Save, CheckCircle2, Settings, Link, Users, ToggleLeft, ToggleRight, X, Trash2, Plus, RefreshCw, AlertTriangle, AlertOctagon, Calendar } from 'lucide-react';

interface AccountSettingsProps {
  currentUser: User;
  onUpdateUser: (updatedUser: User) => void;
}

export const AccountSettings: React.FC<AccountSettingsProps> = ({ currentUser, onUpdateUser }) => {
  // Profile State
  const [profileForm, setProfileForm] = useState({
    firstName: currentUser.firstName || '',
    lastName: currentUser.lastName,
    email: currentUser.email || '',
    phone: currentUser.phone || ''
  });

  // Security State
  const [securityForm, setSecurityForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // System Config State
  const [configForm, setConfigForm] = useState<AppConfig>({
      masterSheetUrl: ''
  });

  // Access Control State (Admin Only)
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [editingAccessUser, setEditingAccessUser] = useState<string | null>(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Add User State
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
      firstName: '',
      lastName: '',
      role: 'Resident' as 'Resident' | 'Attending' | 'Tech' | 'Other'
  });

  const [message, setMessage] = useState<{ text: string, type: 'success' | 'error' } | null>(null);
  
  // Constants
  const ALL_VIEWS = [
      { id: 'dashboard', label: 'Dashboard' },
      { id: 'inpatients', label: 'Inpatient Census' },
      { id: 'reports', label: 'Op Reports' },
      { id: 'caselogs', label: 'Case Logs' },
      { id: 'calendar', label: 'Schedule' },
      { id: 'labs', label: 'Lab Manager' },
      { id: 'education', label: 'Education' },
      { id: 'drugs', label: 'Drug Index' },
  ];

  // Helper to determine admin status including hardcoded email override
  const isAdmin = currentUser.isAdmin || currentUser.id === '8' || currentUser.email === 'kevinsgrisham@gmail.com';

  // Load data on mount
  useEffect(() => {
      const loadData = async () => {
          const config = await dbService.getAppConfig();
          setConfigForm(config);

          if (isAdmin) {
              refreshUserList();
          }
      };
      loadData();
  }, [currentUser, isAdmin]);

  const refreshUserList = async () => {
      setIsLoadingUsers(true);
      try {
          const users = await dbService.getUsers();
          setAllUsers(users);
      } catch (error) {
          console.error("Failed to load users", error);
      } finally {
          setIsLoadingUsers(false);
      }
  };

  const handleSaveProfile = async () => {
    setMessage(null);
    try {
        const updatedUser: User = {
            ...currentUser,
            ...profileForm
        };
        await dbService.updateUser(updatedUser);
        onUpdateUser(updatedUser);
        setMessage({ text: 'Profile updated successfully.', type: 'success' });
    } catch (e) {
        setMessage({ text: 'Failed to update profile.', type: 'error' });
    }
  };

  const handleChangePassword = async () => {
    setMessage(null);
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
         setMessage({ text: 'Auth session invalid. Please re-login.', type: 'error' });
         return;
    }

    if (securityForm.newPassword.length < 6) {
        setMessage({ text: 'New password must be at least 6 characters.', type: 'error' });
        return;
    }

    if (securityForm.newPassword !== securityForm.confirmPassword) {
        setMessage({ text: 'New passwords do not match.', type: 'error' });
        return;
    }

    try {
        await authService.updatePassword(user, securityForm.newPassword);
        setSecurityForm({ newPassword: '', confirmPassword: '' });
        setMessage({ text: 'Password changed successfully.', type: 'success' });
    } catch (e: any) {
        let msg = 'Failed to change password.';
        if (e.code === 'auth/requires-recent-login') msg = 'Please re-login to change password.';
        setMessage({ text: msg, type: 'error' });
    }
  };

  const handleSaveConfig = async () => {
      setMessage(null);
      try {
          await dbService.saveAppConfig(configForm);
          setMessage({ text: 'System configuration updated.', type: 'success' });
      } catch (e) {
          setMessage({ text: 'Failed to save config.', type: 'error' });
      }
  };

  const toggleUserAdmin = async (targetUser: User) => {
      const updatedUser = { ...targetUser, isAdmin: !targetUser.isAdmin };
      setAllUsers(prev => prev.map(u => u.id === targetUser.id ? updatedUser : u));
      await dbService.updateUser(updatedUser);
  };

  const toggleUserViewAccess = async (targetUser: User, viewId: string) => {
      let currentViews = targetUser.allowedViews || ALL_VIEWS.map(v => v.id);
      
      if (currentViews.includes(viewId)) {
          currentViews = currentViews.filter(v => v !== viewId);
      } else {
          currentViews.push(viewId);
      }

      const updatedUser = { ...targetUser, allowedViews: currentViews };
      setAllUsers(prev => prev.map(u => u.id === targetUser.id ? updatedUser : u));
      await dbService.updateUser(updatedUser);
  };

  const handleCreateUser = async () => {
      if (!newUserForm.firstName || !newUserForm.lastName) {
          setMessage({ text: 'First and Last Name are required.', type: 'error' });
          return;
      }

      const newUser: User = {
          id: `user-${Date.now()}`,
          firstName: newUserForm.firstName,
          lastName: newUserForm.lastName,
          role: newUserForm.role,
          allowedViews: ALL_VIEWS.map(v => v.id),
          email: '', // Email will be linked when they sign up
          phone: '',
          isAdmin: false
      };

      setAllUsers(prev => [...prev, newUser]);
      await dbService.adminCreateUser(newUser);
      setIsAddUserOpen(false);
      setNewUserForm({ firstName: '', lastName: '', role: 'Resident' });
      setMessage({ text: 'User profile created. It will auto-link when they sign up.', type: 'success' });
  };

  // Trigger Delete Confirmation
  const initiateDeleteUser = (user: User) => {
      if (user.id === currentUser.id) {
          alert("You cannot delete your own account.");
          return;
      }
      setUserToDelete(user);
  };

  // Execute Deletion
  const confirmDeleteUser = async () => {
      if (!userToDelete) return;
      
      setIsDeleting(true);
      const targetUserId = userToDelete.id;
      const previousUsers = [...allUsers];

      // 1. Optimistic Update
      setAllUsers(prev => prev.filter(u => String(u.id) !== String(targetUserId)));
      
      try {
          // 2. Perform actual delete
          await dbService.deleteUser(targetUserId);
          
          if (editingAccessUser === targetUserId) {
              setEditingAccessUser(null);
          }
          setMessage({ text: 'User deleted successfully.', type: 'success' });
      } catch(err: any) {
          console.error("Delete failed:", err);
          // Rollback UI
          setAllUsers(previousUsers);
          setMessage({ text: 'Failed to delete user from database.', type: 'error' });
      } finally {
          setIsDeleting(false);
          setUserToDelete(null);
      }
  };

  const handleChangeUserRole = async (targetUser: User, newRole: string) => {
      const updatedUser = { ...targetUser, role: newRole as any };
      setAllUsers(prev => prev.map(u => u.id === targetUser.id ? updatedUser : u));
      await dbService.updateUser(updatedUser);
  };

  const handleRestoreDefaults = async () => {
      if(!window.confirm("This will restore the default list of residents and attendings. Any manual changes might be lost or duplicated. Continue?")) return;
      const defaults = await dbService.seedDefaultUsers();
      setAllUsers(defaults);
      setMessage({ text: 'Default user list restored.', type: 'success' });
  }

  const handleRestoreSchedule = async () => {
      if(!window.confirm("This will RESET the rotation schedule to the default template (Jan 2025 - Jan 2027). All custom edits will be lost. Continue?")) return;
      await dbService.seedDefaultSchedule();
      setMessage({ text: 'Schedule reset to default template.', type: 'success' });
      // Reload page to reflect changes in Calendar component without complex lifting state
      setTimeout(() => window.location.reload(), 1000);
  }

  return (
    <div className="h-full flex flex-col p-6 overflow-hidden relative bg-slate-950 text-slate-100">
        <header className="mb-8">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3 brand-font">
                <Shield className="w-8 h-8 text-indigo-400" />
                Account Settings
            </h2>
            <p className="text-slate-400 font-medium text-sm">Manage your profile details and security.</p>
        </header>

        <div className="flex-1 overflow-y-auto custom-scrollbar">
            <div className="max-w-4xl mx-auto space-y-8 pb-10">
                
                {/* Notification Area */}
                {message && (
                    <div className={`p-4 rounded-xl border flex items-center gap-3 animate-in fade-in slide-in-from-top-2 ${message.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
                        {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                        <span className="font-bold text-sm">{message.text}</span>
                    </div>
                )}

                {/* USER ACCESS MANAGEMENT (ADMIN ONLY) */}
                {isAdmin && (
                    <div className="bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-white/5 overflow-hidden ring-1 ring-white/5">
                        <div className="p-6 border-b border-white/5 bg-slate-900/80 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                <Users className="w-5 h-5 text-indigo-400" />
                                User Access Management
                            </h3>
                            <div className="flex items-center gap-2">
                                <button 
                                    onClick={refreshUserList}
                                    className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                                    title="Refresh List"
                                >
                                    <RefreshCw className={`w-4 h-4 ${isLoadingUsers ? 'animate-spin' : ''}`} />
                                </button>
                                <button 
                                    onClick={() => setIsAddUserOpen(true)}
                                    className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold flex items-center gap-1 transition-colors"
                                >
                                    <Plus className="w-4 h-4" /> Add User
                                </button>
                            </div>
                        </div>
                        
                        {isAddUserOpen && (
                            <div className="p-6 border-b border-white/5 bg-indigo-900/20 animate-in slide-in-from-top-2">
                                <h4 className="text-sm font-bold text-white mb-4">Create New User Profile</h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                    <input 
                                        type="text" 
                                        placeholder="First Name" 
                                        className="bg-slate-950 border border-white/10 rounded-lg p-2 text-sm text-white focus:ring-2 focus:ring-indigo-500 outline-none placeholder-slate-600"
                                        value={newUserForm.firstName}
                                        onChange={e => setNewUserForm({...newUserForm, firstName: e.target.value})}
                                    />
                                    <input 
                                        type="text" 
                                        placeholder="Last Name" 
                                        className="bg-slate-950 border border-white/10 rounded-lg p-2 text-sm text-white focus:ring-2 focus:ring-indigo-500 outline-none placeholder-slate-600"
                                        value={newUserForm.lastName}
                                        onChange={e => setNewUserForm({...newUserForm, lastName: e.target.value})}
                                    />
                                    <select 
                                        className="bg-slate-950 border border-white/10 rounded-lg p-2 text-sm text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                        value={newUserForm.role}
                                        onChange={e => setNewUserForm({...newUserForm, role: e.target.value as any})}
                                    >
                                        <option value="Resident">Resident</option>
                                        <option value="Attending">Attending</option>
                                        <option value="Tech">Tech</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={handleCreateUser} className="px-4 py-2 bg-green-600 text-white rounded-lg text-xs font-bold hover:bg-green-500">Create Profile</button>
                                    <button onClick={() => setIsAddUserOpen(false)} className="px-4 py-2 bg-slate-700 text-white rounded-lg text-xs font-bold hover:bg-slate-600">Cancel</button>
                                </div>
                            </div>
                        )}

                        <div className="p-6">
                            <div className="flex flex-col sm:flex-row gap-4 mb-6 justify-center">
                                <button onClick={handleRestoreDefaults} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-bold flex items-center gap-2 border border-slate-700">
                                    <RefreshCw className="w-4 h-4" /> Restore Default Users
                                </button>
                                <button onClick={handleRestoreSchedule} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-bold flex items-center gap-2 border border-slate-700">
                                    <Calendar className="w-4 h-4" /> Restore Default Schedule
                                </button>
                            </div>

                            {allUsers.length === 0 && !isLoadingUsers && (
                                <div className="text-center py-8 text-slate-500">
                                    <p>No users found in database.</p>
                                </div>
                            )}
                            <div className="grid grid-cols-1 gap-4">
                                {allUsers.sort((a,b) => a.lastName.localeCompare(b.lastName)).map(user => (
                                    <div key={user.id} className="bg-slate-900 rounded-xl border border-white/5 overflow-hidden hover:border-white/10 transition-all">
                                        <div className="flex flex-col md:flex-row md:items-center">
                                            {/* Info Area */}
                                            <div 
                                                className="flex-1 p-4 flex items-center gap-4 cursor-pointer hover:bg-white/5 transition-colors z-0"
                                                onClick={() => setEditingAccessUser(editingAccessUser === user.id ? null : user.id)}
                                            >
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shadow-inner ${user.isAdmin ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400'}`}>
                                                    {user.firstName?.charAt(0)}{user.lastName.charAt(0)}
                                                </div>
                                                <div>
                                                    <div className="font-bold text-white text-sm flex items-center gap-2 group-hover:text-indigo-400 transition-colors">
                                                        {user.firstName} {user.lastName}
                                                        {user.id === currentUser.id && <span className="text-[10px] bg-indigo-900/50 text-indigo-300 px-2 py-0.5 rounded-full font-bold border border-indigo-500/20">You</span>}
                                                    </div>
                                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mt-0.5">
                                                        {user.role} {user.isAdmin && <span className="text-indigo-400">• Admin</span>}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Action Area */}
                                            <div className="flex items-center gap-2 p-3 border-t md:border-t-0 md:border-l border-white/5 bg-slate-900/50 md:bg-transparent justify-end md:justify-start relative z-10">
                                                 <button 
                                                    type="button"
                                                    onClick={(e) => { 
                                                        e.preventDefault();
                                                        e.stopPropagation(); 
                                                        initiateDeleteUser(user); 
                                                    }}
                                                    className="p-2 bg-slate-900 text-slate-400 hover:text-white hover:bg-red-600 border border-slate-700 hover:border-red-500 rounded-lg transition-all shadow-sm group/btn cursor-pointer relative z-50"
                                                    title="Delete User"
                                                >
                                                    <Trash2 className="w-4 h-4 pointer-events-none" />
                                                </button>
                                                <button
                                                    type="button" 
                                                    onClick={(e) => { 
                                                        e.preventDefault();
                                                        e.stopPropagation(); 
                                                        setEditingAccessUser(editingAccessUser === user.id ? null : user.id); 
                                                    }}
                                                    className={`flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-lg transition-colors border cursor-pointer relative z-50 ${
                                                        editingAccessUser === user.id 
                                                        ? 'bg-indigo-600 text-white border-indigo-500' 
                                                        : 'text-slate-400 hover:text-white bg-slate-900 hover:bg-slate-800 border-slate-700'
                                                    }`}
                                                >
                                                    {editingAccessUser === user.id ? 'Done' : 'Permissions'}
                                                    <Settings className="w-3 h-3 pointer-events-none" />
                                                </button>
                                            </div>
                                        </div>

                                        {editingAccessUser === user.id && (
                                            <div className="p-4 border-t border-white/5 bg-slate-950/50 animate-in slide-in-from-top-2">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                                    <div className="flex items-center justify-between bg-indigo-900/20 p-3 rounded-lg border border-indigo-500/20">
                                                        <span className="text-sm font-bold text-indigo-300">Administrator Privileges</span>
                                                        <button onClick={() => toggleUserAdmin(user)} className="text-indigo-400 hover:text-white transition-colors">
                                                            {user.isAdmin ? <ToggleRight className="w-8 h-8 text-green-400" /> : <ToggleLeft className="w-8 h-8 text-slate-600" />}
                                                        </button>
                                                    </div>
                                                    
                                                    <div className="flex items-center justify-between bg-slate-900 p-3 rounded-lg border border-white/5">
                                                        <span className="text-sm font-bold text-slate-300">System Role</span>
                                                        <select 
                                                            className="bg-slate-950 border border-white/10 rounded-lg p-1.5 text-xs text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                                                            value={user.role}
                                                            onChange={(e) => handleChangeUserRole(user, e.target.value)}
                                                        >
                                                            <option value="Resident">Resident</option>
                                                            <option value="Attending">Attending</option>
                                                            <option value="Tech">Tech</option>
                                                            <option value="Other">Other</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                
                                                <h4 className="text-xs font-bold text-slate-500 uppercase mb-3 ml-1">Module Access</h4>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
                                                    {ALL_VIEWS.map(view => {
                                                        const hasAccess = !user.allowedViews || user.allowedViews.includes(view.id);
                                                        return (
                                                            <button 
                                                                key={view.id}
                                                                onClick={() => toggleUserViewAccess(user, view.id)}
                                                                className={`p-2 rounded-lg text-xs font-bold border flex items-center justify-between transition-all ${
                                                                    hasAccess 
                                                                    ? 'bg-slate-800 border-indigo-500/50 text-white shadow-sm' 
                                                                    : 'bg-slate-950 border-white/5 text-slate-600 opacity-60'
                                                                }`}
                                                            >
                                                                {view.label}
                                                                {hasAccess ? <CheckCircle2 className="w-3 h-3 text-indigo-400" /> : <X className="w-3 h-3" />}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Profile Section */}
                <div className="bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-white/5 overflow-hidden shadow-sm">
                    <div className="p-6 border-b border-white/5 bg-slate-900/50">
                        <h3 className="text-lg font-bold text-white flex items-center gap-2">
                            <UserIcon className="w-5 h-5 text-indigo-400" />
                            Profile Information
                        </h3>
                    </div>
                    <div className="p-8 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                             <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-2">First Name</label>
                                <input 
                                    type="text" 
                                    value={profileForm.firstName}
                                    onChange={(e) => setProfileForm({...profileForm, firstName: e.target.value})}
                                    className="w-full p-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:ring-2 focus:ring-indigo-500/50 outline-none placeholder-slate-600"
                                    placeholder="First Name"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Last Name</label>
                                <input 
                                    type="text" 
                                    value={profileForm.lastName}
                                    onChange={(e) => setProfileForm({...profileForm, lastName: e.target.value})}
                                    className="w-full p-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:ring-2 focus:ring-indigo-500/50 outline-none placeholder-slate-600"
                                    placeholder="Last Name"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Email Address</label>
                            <div className="relative">
                                <input 
                                    type="email" 
                                    value={profileForm.email}
                                    onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                                    className="w-full p-3 pl-10 bg-slate-950 border border-white/10 rounded-xl text-white focus:ring-2 focus:ring-indigo-500/50 outline-none placeholder-slate-600"
                                    placeholder="email@example.com"
                                />
                                <Mail className="w-4 h-4 text-slate-500 absolute left-3.5 top-3.5" />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Role (Read Only)</label>
                            <div className="inline-flex items-center px-4 py-2 rounded-lg bg-indigo-900/30 border border-indigo-500/30 text-indigo-300 font-bold text-sm">
                                {currentUser.role}
                            </div>
                        </div>

                        <div className="flex justify-end pt-4 border-t border-white/5">
                            <button 
                                onClick={handleSaveProfile}
                                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl flex items-center gap-2 transition-all shadow-lg shadow-indigo-900/20"
                            >
                                <Save className="w-4 h-4" /> Save Profile
                            </button>
                        </div>
                    </div>
                </div>

                {/* System Configuration (Admins Only) */}
                {isAdmin && (
                    <div className="bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-white/5 overflow-hidden shadow-sm">
                        <div className="p-6 border-b border-white/5 bg-slate-900/50">
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                <Settings className="w-5 h-5 text-indigo-400" />
                                System Configuration
                            </h3>
                        </div>
                        <div className="p-8 space-y-6">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Master On-Call Sheet URL</label>
                                <div className="relative">
                                    <input 
                                        type="text" 
                                        value={configForm.masterSheetUrl}
                                        onChange={(e) => setConfigForm({...configForm, masterSheetUrl: e.target.value})}
                                        className="w-full p-3 pl-10 bg-slate-950 border border-white/10 rounded-xl text-white focus:ring-2 focus:ring-indigo-500/50 outline-none placeholder-slate-600"
                                        placeholder="https://..."
                                    />
                                    <Link className="w-4 h-4 text-slate-500 absolute left-3.5 top-3.5" />
                                </div>
                                <p className="text-xs text-slate-500 mt-2">This link updates the button on the Case Logs page for all users.</p>
                            </div>

                            <div className="flex justify-end pt-4 border-t border-white/5">
                                <button 
                                    onClick={handleSaveConfig}
                                    className="px-6 py-2.5 bg-teal-600 hover:bg-teal-500 text-white font-bold rounded-xl flex items-center gap-2 transition-all shadow-lg shadow-teal-900/20"
                                >
                                    <Save className="w-4 h-4" /> Update System Config
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Security Section */}
                <div className="bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-white/5 overflow-hidden shadow-sm">
                    <div className="p-6 border-b border-white/5 bg-slate-900/50">
                        <h3 className="text-lg font-bold text-white flex items-center gap-2">
                            <Lock className="w-5 h-5 text-indigo-400" />
                            Security & Password
                        </h3>
                    </div>
                    <div className="p-8 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                             <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-2">New Password</label>
                                <input 
                                    type="password" 
                                    value={securityForm.newPassword}
                                    onChange={(e) => setSecurityForm({...securityForm, newPassword: e.target.value})}
                                    className="w-full p-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:ring-2 focus:ring-indigo-500/50 outline-none placeholder-slate-600"
                                    placeholder="Enter new password"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Confirm New Password</label>
                                <input 
                                    type="password" 
                                    value={securityForm.confirmPassword}
                                    onChange={(e) => setSecurityForm({...securityForm, confirmPassword: e.target.value})}
                                    className="w-full p-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:ring-2 focus:ring-indigo-500/50 outline-none placeholder-slate-600"
                                    placeholder="Confirm new password"
                                />
                            </div>
                        </div>

                         <div className="flex justify-end pt-4 border-t border-white/5">
                            <button 
                                onClick={handleChangePassword}
                                disabled={!securityForm.newPassword}
                                className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl flex items-center gap-2 transition-all border border-white/10"
                            >
                                <Shield className="w-4 h-4" /> Update Password
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Delete Confirmation Modal */}
        {userToDelete && (
            <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                <div className="bg-slate-900 rounded-2xl border border-white/10 shadow-2xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-200">
                    <div className="w-12 h-12 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-500/30">
                        <AlertOctagon className="w-6 h-6 text-red-500" />
                    </div>
                    <h3 className="text-xl font-bold text-white text-center mb-2">Delete User?</h3>
                    <p className="text-slate-400 text-sm text-center mb-6 leading-relaxed">
                        Are you sure you want to delete <span className="font-bold text-white">{userToDelete.firstName} {userToDelete.lastName}</span>? This action is permanent.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                        <button 
                            onClick={() => setUserToDelete(null)}
                            disabled={isDeleting}
                            className="px-4 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-bold transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={confirmDeleteUser}
                            disabled={isDeleting}
                            className="px-4 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl font-bold transition-colors shadow-lg shadow-red-900/20 flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {isDeleting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            {isDeleting ? 'Deleting...' : 'Delete'}
                        </button>
                    </div>
                </div>
            </div>
        )}
    </div>
  );
};
